<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <!-- Loading Overlay -->
    <div 
      v-if="uiStore.loading" 
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p class="mt-3 text-sm text-gray-600 dark:text-gray-300 font-medium">Loading...</p>
      </div>
    </div>

    <!-- Notifications -->
    <div 
      v-if="uiStore.notifications.length > 0"
      class="fixed top-4 right-4 z-40 space-y-2"
    >
      <div 
        v-for="notification in uiStore.notifications" 
        :key="notification.id"
        class="bg-white dark:bg-gray-800 border-l-4 p-4 shadow-xl rounded-r-2xl max-w-sm border border-gray-100 dark:border-gray-700"
        :class="{
          'border-l-green-500': notification.type === 'success',
          'border-l-red-500': notification.type === 'error',
          'border-l-blue-500': notification.type === 'info',
          'border-l-yellow-500': notification.type === 'warning'
        }"
      >
        <div class="flex">
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ notification.title }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ notification.message }}</p>
          </div>
          <button 
            @click="uiStore.removeNotification(notification.id)"
            class="ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Beta Tag -->
    <BetaTag />

    <!-- Main App Content -->
    <router-view />
    
    <!-- Cookie Consent Dialog -->
    <CookieConsent />
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { useSettingsStore } from '@/stores/settings'
import CookieConsent from '@/components/CookieConsent.vue'
import BetaTag from '@/components/layout/BetaTag.vue'

const authStore = useAuthStore()
const uiStore = useUIStore()
const settingsStore = useSettingsStore()

onMounted(async () => {
  // Initialize auth state from localStorage
  authStore.initializeAuth()
  
  // Initialize UI theme
  uiStore.initializeTheme()
  
  // Fetch global settings
  await settingsStore.fetchSettings()
})
</script>

<style>
#app {
  font-family: 'Inter', sans-serif;
  @apply transition-colors duration-300;
}

.font-heading {
  font-family: 'Poppins', sans-serif;
}

/* Custom scrollbar - light */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Custom scrollbar - dark */
.dark ::-webkit-scrollbar-track {
  background: #1f2937;
}

.dark ::-webkit-scrollbar-thumb {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
